/**
 * Order Booking Creatives — CST / ops admin panel configuration.
 *
 * Single source of truth for the content status model, sub-types, tabs and
 * endpoint paths described in the shared API contract. Nothing in this folder
 * should hard-code a status number or an endpoint string.
 *
 * This status model is its OWN namespace and must never be confused with the
 * designer panel's 1-8 design-status model that lives in the same shared `bsc`
 * row and the same shared timeline table.
 *
 * Difference from the sibling content-writer panel: this admin view also sees
 * creatives that have NO content writer yet. On the wire those rows carry
 * `content_status: null` (the field is named `content_status` here, not
 * `status`), which this config models as the status-0 "Unassigned"
 * pseudo-status. Everything downstream — pills, tabs, permissions — reads that
 * pseudo-status, so `null` never has to be special-cased in a component.
 */

export const STATUS = {
  0: { value: 0, key: 'unassigned', label: 'Unassigned', pill: 'bg-slate-100 text-slate-600 ring-slate-200' },
  1: { value: 1, key: 'assigned', label: 'Assigned', pill: 'bg-amber-50 text-amber-700 ring-amber-200' },
  2: { value: 2, key: 'picked', label: 'Picked', pill: 'bg-sky-50 text-sky-700 ring-sky-200' },
  3: { value: 3, key: 'submitted', label: 'Submitted', pill: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  4: { value: 4, key: 'rejected', label: 'Rejected', pill: 'bg-rose-50 text-rose-700 ring-rose-200' },
  5: { value: 5, key: 'approved', label: 'Approved', pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
};

/** The pseudo-status a row with no content writer resolves to. */
export const UNASSIGNED_STATUS = STATUS[0];

/**
 * `null` / `undefined` / `''` all mean "no writer assigned yet" — they map to
 * status 0, NOT to an unknown/error state. Anything else falls back to 0 too,
 * so an unexpected value still renders a sane pill instead of crashing.
 */
export const getStatus = (status) => {
  if (status === null || status === undefined || status === '') return STATUS[0];
  return STATUS[Number(status)] || STATUS[0];
};

export const SUB_TYPE_LABELS = {
  full_image: 'Full Image',
  full_video: 'Full Video',
  promoted_image_story: 'Promoted Image Story',
  promoted_video_story: 'Promoted Video Story',
};

export const VIDEO_SUB_TYPES = ['full_video', 'promoted_video_story'];

export const PROMOTED_SUB_TYPES = ['promoted_image_story', 'promoted_video_story'];

export const SUB_TYPE_OPTIONS = Object.keys(SUB_TYPE_LABELS).map((value) => ({
  value,
  label: SUB_TYPE_LABELS[value],
}));

export const TABS = [
  { key: 'unassigned', label: 'Unassigned', status: 0 },
  { key: 'assigned', label: 'Assigned', status: 1 },
  { key: 'picked', label: 'Picked', status: 2 },
  { key: 'submitted', label: 'Submitted', status: 3 },
  { key: 'rejected', label: 'Rejected', status: 4 },
  { key: 'approved', label: 'Approved', status: 5 },
];

export const TAB_KEYS = TABS.map((t) => t.key);

export const DEFAULT_TAB = 'unassigned';

export const getTab = (key) => TABS.find((t) => t.key === key) || TABS[0];

/** Short, tab-specific empty-state copy (never a generic "No data"). */
export const TAB_EMPTY_COPY = {
  unassigned: {
    title: 'Everything is assigned',
    line: 'New order-booking creatives that still need a content writer will land here.',
  },
  assigned: {
    title: 'No one is sitting on a new brief',
    line: 'Creatives you have handed to a content writer stay here until the writer picks them up.',
  },
  picked: {
    title: 'No writing in progress',
    line: 'Once a content writer picks up a creative it moves here while the drafts are being written.',
  },
  submitted: {
    title: 'Nothing waiting for review',
    line: 'Drafts submitted by content writers show up here for you to approve or send back.',
  },
  rejected: {
    title: 'Nothing sent back for rework',
    line: 'Creatives you rejected stay here until the writer resubmits a fresh set of drafts.',
  },
  approved: {
    title: 'No approved content yet',
    line: 'Every creative you sign off on is collected here with the variation you picked.',
  },
};

export const ENDPOINTS = {
  contentWriters: '/user/booking-content-admin/content-writers',
  counts: '/user/booking-content-admin/counts',
  list: '/user/booking-content-admin/list',
  assign: '/user/booking-content-admin/assign',
  approve: '/user/booking-content-admin/approve',
  reject: '/user/booking-content-admin/reject',
  logs: '/user/booking-content-admin/logs',
  approveRequest: '/user/booking-content-admin/approve-request',
  rejectRequest: '/user/booking-content-admin/reject-request',
  message: '/user/booking-content-admin/message',
  uploadAttachment: '/user/booking-content-admin/upload-attachment',
  versions: '/user/booking-content-admin/versions',
};

export const PAGE_SIZE = 12;

/** localStorage key holding `{ tab, filters }`. */
export const STORAGE_KEY = 'ob_content_admin_filters';

/* ------------------------------------------------------------------ */
/* Permissions — mirrors the status/action matrix in the shared spec.  */
/* ------------------------------------------------------------------ */

/**
 * Approve / reject are only offered on a submitted draft (status 3).
 *
 * The `!= null` guard is not redundant: `Number(null)` is `0`, and `Number('')`
 * is `0` too, so a naive `Number(status) === 3` would be fine today but would
 * quietly break the day someone renumbers a status. Written this way an
 * unassigned row can never reach a review action.
 */
export const canApprove = (status) =>
  status !== null && status !== undefined && status !== '' && Number(status) === 3;

export const canReject = (status) =>
  status !== null && status !== undefined && status !== '' && Number(status) === 3;

// `/assign` is an UPSERT on the backend — assigning and re-assigning are the
// same call, and it carries no status gate. Ops can therefore hand a creative
// to a different writer at any point, including after approval. Do not invent
// a gate that isn't in the API contract.
export const canAssign = () => true;

/** True once a content writer owns this creative (i.e. it is past status 0). */
export const isAssigned = (row) => row?.content_status != null;

export const isVideoSubType = (subType) => VIDEO_SUB_TYPES.includes(String(subType || ''));

export const isPromotedSubType = (subType) => PROMOTED_SUB_TYPES.includes(String(subType || ''));

export const subTypeLabel = (subType) => SUB_TYPE_LABELS[subType] || subType || '—';

/**
 * The three fields a draft variation can carry. Every sub-type may use any
 * of them — the backend only requires at least one non-empty field per
 * variation, so the review UI does not gate fields by sub-type either.
 */
export const VARIATION_FIELDS = [
  { key: 'head_line', label: 'Headline', kind: 'text', placeholder: 'Write a headline…' },
  { key: 'description', label: 'Description', kind: 'textarea', placeholder: 'Write a description…' },
  { key: 'text_in_link', label: 'Link text', kind: 'text', placeholder: 'Write the link text…' },
];

/** True when at least one of the three fields carries real text. */
export const isVariationFilled = (variation) =>
  VARIATION_FIELDS.some((f) => String(variation?.[f.key] || '').trim());

/* ------------------------------------------------------------------ */
/* Formatting helpers — every timestamp on the wire is epoch seconds.  */
/* ------------------------------------------------------------------ */

const toDate = (sec) => {
  const n = Number(sec);
  if (!n || Number.isNaN(n) || n <= 0) return null;
  return new Date(n * 1000);
};

export const formatEpoch = (sec) => {
  const d = toDate(sec);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const formatEpochDate = (sec) => {
  const d = toDate(sec);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

export const formatEpochTime = (sec) => {
  const d = toDate(sec);
  if (!d) return '';
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

/** "just now" / "4m" / "3h" / "6d" / falls back to a date past 30 days. */
export const formatRelative = (sec) => {
  const d = toDate(sec);
  if (!d) return '—';
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 0) return 'just now';
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return formatEpochDate(sec);
};

const IMAGE_RE = /\.(jpe?g|png|gif|webp|avif|svg)(\?.*)?$/i;
const VIDEO_RE = /\.(mp4|webm|ogg|mov|avi|m4v)(\?.*)?$/i;

export const looksLikeImage = (url) => IMAGE_RE.test(String(url || ''));
export const looksLikeVideo = (url) => VIDEO_RE.test(String(url || ''));

/** Normalised, stable shape for the filter bar — also the localStorage shape. */
export const EMPTY_FILTERS = {
  query: '',
  sub_type: '',
  start_date: '',
  end_date: '',
  sort_by_latest_activity: false,
};

// NOTE: `query` is deliberately NOT trimmed here — trimming a committed value
// would yank a trailing space back out of the search box while the user types.
// The trim happens once, at the API boundary.
export const normaliseFilters = (filters) => ({
  query: String(filters?.query || ''),
  sub_type: filters?.sub_type || '',
  start_date: filters?.start_date || '',
  end_date: filters?.end_date || '',
  sort_by_latest_activity: !!filters?.sort_by_latest_activity,
});

/**
 * Primitive, order-stable key for a filter object. Effects depend on THIS,
 * never on the object itself — object literals in deps re-run every render.
 */
export const filterKeyOf = (filters) => JSON.stringify(normaliseFilters(filters));

export const hasActiveFilters = (filters) => {
  const f = normaliseFilters(filters);
  return !!(
    f.query.trim() ||
    f.sub_type ||
    f.start_date ||
    f.end_date ||
    f.sort_by_latest_activity
  );
};
